import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Plus } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { CardApi } from "../../api/services";
import { errorMessage } from "../../api/client";
import { Field, PageHeader, Spinner, StatusBadge } from "../../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { peso } from "../../lib/format";

const QR_PREFIX = "FASTPASS-PKG:";

function PackageDialog({ pkg, onClose }: { pkg: any; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<any>({ name: pkg?.name || "", price: pkg ? pkg.price / 100 : "", duration_days: pkg?.duration_days || 365,
    discount_pct: pkg?.discount_pct || 0, benefits: (pkg?.benefits || []).join("\n"), active: pkg?.active ?? true,
    free_rides: pkg?.free_rides ?? 2, insurance: pkg?.insurance ?? "Philbritish Insurance" });
  const save = useMutation({
    mutationFn: () => {
      const body = { ...f, price: Math.round(Number(f.price) * 100), duration_days: Number(f.duration_days), discount_pct: Number(f.discount_pct), free_rides: Number(f.free_rides),
        benefits: f.benefits.split("\n").map((s: string) => s.trim()).filter(Boolean) };
      return pkg ? CardApi.updatePackage(pkg.id, body) : CardApi.createPackage(body);
    },
    onSuccess: () => { toast.success("Package saved"); qc.invalidateQueries({ queryKey: ["admin", "packages"] }); onClose(); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="package-dialog">
        <DialogHeader><DialogTitle>{pkg ? "Edit package" : "New membership package"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Package name"><input className="fp-input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="package-name-input" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Price (₱)"><input className="fp-input" type="number" min="1" step="0.01" required value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="package-price-input" /></Field>
            <Field label="Days"><input className="fp-input" type="number" min="1" required value={f.duration_days} onChange={(e) => setF({ ...f, duration_days: e.target.value })} data-testid="package-days-input" /></Field>
            <Field label="Fare discount %"><input className="fp-input" type="number" min="0" max="100" value={f.discount_pct} onChange={(e) => setF({ ...f, discount_pct: e.target.value })} data-testid="package-discount-input" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Free rides per year"><input className="fp-input" type="number" min="0" max="50" value={f.free_rides} onChange={(e) => setF({ ...f, free_rides: e.target.value })} data-testid="package-free-rides-input" /></Field>
            <Field label="Insurance partner"><input className="fp-input" value={f.insurance} onChange={(e) => setF({ ...f, insurance: e.target.value })} data-testid="package-insurance-input" /></Field>
          </div>
          <Field label="Benefits (one per line)"><textarea className="fp-input h-24 py-2" value={f.benefits} onChange={(e) => setF({ ...f, benefits: e.target.value })} data-testid="package-benefits-input" /></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="package-active-checkbox" /> Active (can be registered)</label>
          <button className="fp-btn-primary w-full" disabled={save.isPending} data-testid="package-save-button">Save package</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PackageQr({ p }: { p: any }) {
  const download = () => {
    const canvas = document.getElementById(`qr-${p.id}`) as HTMLCanvasElement;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `fastpass-package-${p.qr_code}.png`;
    a.click();
  };
  return (
    <div className="flex flex-col items-center gap-2">
      <QRCodeCanvas id={`qr-${p.id}`} value={QR_PREFIX + p.qr_code} size={132} includeMargin fgColor="#003F32" data-testid={`package-qr-${p.id}`} />
      <p className="font-mono text-xs text-slate-600">{p.qr_code}</p>
      <button className="fp-btn-secondary min-h-[36px] px-3 text-xs" onClick={download} data-testid={`package-qr-download-${p.id}`}><Download size={14} /> PNG</button>
    </div>
  );
}

export default function AdminPackages() {
  const [editing, setEditing] = useState<any>(undefined);
  const { data, isLoading } = useQuery({ queryKey: ["admin", "packages"], queryFn: CardApi.packages });
  return (
    <div className="space-y-4">
      <PageHeader title="Membership packages" subtitle="Each package has a unique QR code that customers scan during registration."
        action={<button className="fp-btn-primary" onClick={() => setEditing(null)} data-testid="package-add-button"><Plus size={16} /> New package</button>} />
      {isLoading ? <Spinner /> : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {(data || []).map((p) => (
            <div key={p.id} className="fp-card flex gap-4" data-testid="package-card">
              <div className="min-w-0 flex-1 space-y-1">
                <p className="font-display text-lg font-bold">{p.name}</p>
                <p className="text-2xl font-extrabold text-[#036248]">{peso(p.price)}</p>
                <p className="text-xs text-slate-500">{p.duration_days} days · {p.discount_pct}% fare discount · {p.free_rides ?? 0} free rides{p.insurance ? ` · ${p.insurance}` : ""}</p>
                <StatusBadge status={p.active ? "active" : "offline"} label={p.active ? "active" : "inactive"} />
                <ul className="pt-1 text-xs text-slate-600">{p.benefits.map((b: string) => <li key={b}>• {b}</li>)}</ul>
                <button className="fp-btn-secondary mt-2 min-h-[36px] px-3 text-xs" onClick={() => setEditing(p)} data-testid={`package-edit-${p.id}`}>Edit</button>
              </div>
              <PackageQr p={p} />
            </div>
          ))}
          {!data?.length && <p className="text-sm text-slate-500" data-testid="packages-empty">No packages yet.</p>}
        </div>
      )}
      {editing !== undefined && <PackageDialog pkg={editing} onClose={() => setEditing(undefined)} />}
    </div>
  );
}
